import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import {
  QaiBreadcrumbListComponent,
  BreadcrumbItem,
  QaiInnerWrapComponent,
  QaiLoadingComponent,
  QaiFlexContainerComponent,
  QaiMultiSelectComponent,
  QaiLabelComponent,
} from '@qai/ui';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AccountFeatureService } from 'src/app/services/account-feature/account-feature.service';
import { AppConstants } from 'src/app/app-constants';
import { SimulationSessionsComponent } from './simulation-sessions/simulation-sessions.component';
import { MethodologyAveragesComponent } from './methodology-averages/methodology-averages.component';
import { MessagingAveragesComponent } from './messaging-averages/messaging-averages.component';
import { SkillsAveragesComponent } from './skills-averages/skills-averages.component';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { IAssignmentViewStatsRequest } from 'src/app/interfaces/reportingV2/iassignment-view-stats-request';
import { IAssignmentViewStatsResponse } from 'src/app/interfaces/reportingV2/iassignment-view-stats-response';
import { forkJoin } from 'rxjs';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { EntityOption } from 'src/app/interfaces/reportingV2/ientity-option';
import { AccountEntityUtil } from 'src/app/utils/account-entity-util';
import { ReportingNavigationResetService } from 'src/app/services/reporting-dashboard/reporting-navigation-reset.service';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { getAccountNameByUuid } from 'src/app/utils/local-storage.util';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';

@Component({
  selector: 'app-simulation-detail',
  templateUrl: './simulation-detail.component.html',
  styleUrls: ['./simulation-detail.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    },
  ],
  imports: [
    CommonModule,
    TranslocoModule,
    QaiBreadcrumbListComponent,
    QaiInnerWrapComponent,
    QaiLabelComponent,
    QaiMultiSelectComponent,
    SimulationSessionsComponent,
    QaiLoadingComponent,
    MethodologyAveragesComponent,
    MessagingAveragesComponent,
    SkillsAveragesComponent,
    QaiFlexContainerComponent
  ],
})

export class SimulationDetailComponent implements OnInit {
  isTranslationLoaded = false;
  simulationUuid: string | null = null;
  assignmentName: string = '';
  simulationName: string = '';
  accountName: string = '';
  moduleAssignmentUuid: string = '';
  accountUuid: string = '';
  selectedTeamUuids: string[] = [];
  teamOptions: EntityOption[] = [];
  isLoadingTeams = false;
  clearTeamsOption = { enable: true, label: 'Clear teams' };
  visibleOptionsMode = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: 'Select all' },
    deselectAllOption: { value: 'deselectAll', label: 'Deselect all' }
  };
  sessionStats: IAssignmentViewStatsResponse[] = [];
  isError: boolean = false;
  hideIndividualUserData: boolean = false;
  isLoading: boolean = false;
  isDataLoading: boolean = false;
  promptType: string[] = [];
  engagementStatus: string | null = null;

  constructor(
    private amplitudeService: AmplitudeService,
    private route: ActivatedRoute,
    private router: Router,
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private featureFlagsService: FeatureFlagsService,
    private accountFeatureService: AccountFeatureService,
    private reportingDashboardService: ReportingDashboardService,
    private readonly reportingNavigationResetService: ReportingNavigationResetService,
  ) { }

  async ngOnInit(): Promise<void> {
    this.reportingNavigationResetService.init();
    this.isLoading = true;

    this.simulationUuid = this.route.snapshot.paramMap.get('id');
    this.moduleAssignmentUuid = this.route.snapshot.queryParamMap.get('moduleAssignmentUuid') ?? '';
    this.selectedTeamUuids = [];

    const state = history.state;
    if (state) {
      this.engagementStatus = state['engagementStatus'] || null;
      this.simulationName = state['simulationName'] ?? '';
      this.assignmentName = state['assignmentName'] ?? '';
    }

    const isFeatureFlagEnabled = await this.featureFlagsService.isFlagEnabled(
      AppConstants.SIMULATION_REPORT_VIEW
    );

    if (!isFeatureFlagEnabled) {
      this.router.navigate(['/reporting_dashboard']);
      return;
    }

    this.hideIndividualUserData = await this.accountFeatureService.isFeatureActive(AppConstants.ACCOUNT_FEATURE_HIDE_INDIVIDUAL_USER_DATA);

    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
      this.clearTeamsOption.label = this.translocoService.translate('CLEAR_TEAMS', {}, 'reportingV2');
      this.visibleOptionsMode.selectAllOption.label = this.translocoService.translate('SELECT_ALL', {}, 'reportingV2');
      this.visibleOptionsMode.deselectAllOption.label = this.translocoService.translate('DESELECT_ALL', {}, 'reportingV2');
    });

    const accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();

    if (!accountUuid || !this.moduleAssignmentUuid || !this.simulationUuid) {
      console.error('Missing required parameters:', {
        accountUuid: !!accountUuid,
        moduleAssignmentUuid: !!this.moduleAssignmentUuid,
        simulationUuid: !!this.simulationUuid
      });
      this.isLoading = false;
      this.isError = true;
      return;
    }

    this.accountUuid = accountUuid;
    this.accountName = getAccountNameByUuid(accountUuid);
    this.selectedTeamUuids = ReportingLocalStorageUtil.getStoredTeamsFilter(this.accountUuid) ?? [];
    this.loadTeams();
    this.loadSimulationData(true);
  }

  private loadTeams(): void {
    this.isLoadingTeams = true;
    if (!this.accountUuid) {
      this.isLoadingTeams = false;
      return;
    }

    const demoOpts = withDemoModule(AppConstants.NEW_REPORTING_UI);
    this.reportingDashboardService
      .getTeams(this.accountUuid, demoOpts)
      .subscribe({
        next: (teams) => {
          this.teamOptions = AccountEntityUtil.formatEntities(teams);
          this.isLoadingTeams = false;
        },
        error: (error) => {
          console.error('Error loading teams for simulation detail:', error);
          this.isLoadingTeams = false;
        },
      });
  }

  private loadSimulationData(showPageLoader: boolean = false): void {
    if (!this.accountUuid || !this.moduleAssignmentUuid || !this.simulationUuid) {
      return;
    }

    if (showPageLoader) {
      this.isLoading = true;
    } else {
      this.isDataLoading = true;
    }
    this.isError = false;

    const sessionStatsPayload: IAssignmentViewStatsRequest = {
      accountUuid: this.accountUuid,
      moduleAssignmentUuid: this.moduleAssignmentUuid,
      simulationUuid: this.simulationUuid,
      teamUuids: this.selectedTeamUuids,
    };

    const demoOpts = withDemoModule(AppConstants.NEW_REPORTING_UI);
    forkJoin({
      simInfo: this.reportingDashboardService.getSimulationStats(
        this.accountUuid,
        this.moduleAssignmentUuid,
        this.simulationUuid,
        this.selectedTeamUuids,
        demoOpts
      ),
      sessionStats: this.reportingDashboardService.getAssignmentViewStats(sessionStatsPayload, demoOpts),
    }).subscribe({
      next: (result) => {
        const simInfo = result.simInfo;
        if (simInfo) {
          this.simulationName = simInfo.simulationName ?? this.simulationName;
          this.assignmentName = simInfo.moduleAssignmentName ?? this.assignmentName;
          this.promptType = simInfo.promptType ?? [];
        }
        this.sessionStats = result.sessionStats ?? [];

        if (showPageLoader) {
          this.isLoading = false;
        } else {
          this.isDataLoading = false;
        }

        this.amplitudeService.trackEvent(
          AmplitudeTrackingEvents.SIMULATION_REPORT_PAGEVIEW,
          {
            simulation_name: this.simulationName,
          }
        );
      },
      error: (err) => {
        this.isError = true;
        if (showPageLoader) {
          this.isLoading = false;
        } else {
          this.isDataLoading = false;
        }
        console.error('Failed to fetch simulation data', err);
      },
    });
  }

  onTeamChange(teamUuids: string[]): void {
    this.selectedTeamUuids = teamUuids ?? [];
    ReportingLocalStorageUtil.setStoredTeamsFilter(this.selectedTeamUuids, this.accountUuid);
    this.loadSimulationData(false);
  }

  getBreadcrumbItems(): BreadcrumbItem[] {
    const assignmentTab = ReportingLocalStorageUtil.getStoredAssignmentTab();
    const assignmentViewLink = (() => {
      // SAFE: fixed internal constant
      if (assignmentTab === AppConstants.TYPE_PRACTICE.toLowerCase() && this.moduleAssignmentUuid) {
        const status = this.engagementStatus ?? '';
        return `/reporting_dashboard/practice/${this.moduleAssignmentUuid}?engagement_status=${status}`;
      }

      // SAFE: fixed internal constant
      if (assignmentTab === AppConstants.TYPE_CERTIFICATION.toLowerCase() && this.moduleAssignmentUuid) {
        const status = this.engagementStatus ?? '';
        return `/reporting_dashboard/certification/${this.moduleAssignmentUuid}?certification_status=${status}`;
      }
  
      // Fallback: redirect to dashboard.
      return '/reporting_dashboard';
    })();
  
    return [
      {
        text: this.translocoService.translate('DASHBOARD', {}, 'reportingV2'),
        href: '/reporting_dashboard',
      },
      {
        text: this.assignmentName,
        href: assignmentViewLink,
      },
    ];
  }

  get isMessageAdherenceV2Enabled(): boolean {
    return this.promptType.includes(SimulationConstants.MESSAGING_PROMPT_TYPE_V2);
  }

  get shouldShowMethodologyAverages(): boolean {
    return this.promptType.includes(SimulationConstants.METHODOLOGY_PROMPT_TYPE);
  }

  get shouldShowMessagingAverages(): boolean {
    return this.promptType.includes(SimulationConstants.MESSAGING_PROMPT_TYPE_V2) ||
           this.promptType.includes(SimulationConstants.MESSAGING_PROMPT_TYPE);
  }

  get shouldShowSkillsAverages(): boolean {
    return this.promptType.includes(SimulationConstants.SKILL_PROMPT_TYPE);
  }
}
